import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { stockPriceAPI } from '@/features/assets/api/api'
import { fetchBinancePricesRest } from '@/lib/binance'
import type { StockPrice, CryptoPrice, FxRate } from '@/features/assets/api/types'
import type { RootState } from './store'

type Status = 'idle' | 'loading' | 'succeeded' | 'failed'

interface PricesState {
  // Stock prices (VND)
  stockPrices: Record<string, number>
  stockUpdatedAt: Record<string, string>
  stockStatus: Status
  stockLastFetchedAt: number | null

  // Crypto prices (USD)
  cryptoPrices: Record<string, number>
  cryptoUpdatedAt: Record<string, string>
  cryptoStatus: Status
  cryptoLastFetchedAt: number | null

  // FX rates
  fxRates: Record<string, number>
  fxUpdatedAt: Record<string, string>
  fxStatus: Status
  fxLastFetchedAt: number | null
}

const initialState: PricesState = {
  stockPrices: {},
  stockUpdatedAt: {},
  stockStatus: 'idle',
  stockLastFetchedAt: null,

  cryptoPrices: {},
  cryptoUpdatedAt: {},
  cryptoStatus: 'idle',
  cryptoLastFetchedAt: null,

  fxRates: {},
  fxUpdatedAt: {},
  fxStatus: 'idle',
  fxLastFetchedAt: null,
}

export const fetchStockPrices = createAsyncThunk(
  'prices/fetchStock',
  async (_, { getState }) => {
    const { assets } = getState() as RootState
    const symbols = assets.items
      .filter((a) => a.type === 'stock' && a.symbol)
      .map((a) => a.symbol!)
    if (symbols.length === 0) return [] as StockPrice[]
    return await stockPriceAPI.getPrices(symbols)
  },
  {
    condition: (_, { getState }) => {
      const { prices } = getState() as RootState
      return prices.stockStatus !== 'loading'
    },
  },
)

export const fetchCryptoPrices = createAsyncThunk(
  'prices/fetchCrypto',
  async (_, { getState }) => {
    const { assets } = getState() as RootState
    const symbols = assets.items
      .filter((a) => a.type === 'crypto' && a.symbol)
      .map((a) => a.symbol!)
    if (symbols.length === 0) return [] as CryptoPrice[]
    // Ưu tiên gọi Binance trực tiếp (IP user, ít bị chặn hơn Worker).
    // Fallback về Worker nếu direct fail.
    try {
      return await fetchBinancePricesRest(symbols)
    } catch {
      return await stockPriceAPI.getCryptoPrices(symbols)
    }
  },
  {
    condition: (_, { getState }) => {
      const { prices } = getState() as RootState
      return prices.cryptoStatus !== 'loading'
    },
  },
)

export const fetchFxRates = createAsyncThunk(
  'prices/fetchFx',
  async () => {
    return await stockPriceAPI.getFxRates(['USDVND', 'VNDUSD'])
  },
  {
    condition: (_, { getState }) => {
      const { prices } = getState() as RootState
      return prices.fxStatus !== 'loading'
    },
  },
)

const pricesSlice = createSlice({
  name: 'prices',
  initialState,
  reducers: {
    invalidatePrices: (state) => {
      state.stockLastFetchedAt = null
      state.cryptoLastFetchedAt = null
    },
    /** WebSocket push single crypto tick — không đụng status/lastFetchedAt */
    upsertCryptoPrice: (state, action: PayloadAction<CryptoPrice>) => {
      const { symbol, price, updated_at } = action.payload
      state.cryptoPrices[symbol] = price
      state.cryptoUpdatedAt[symbol] = updated_at
    },
  },
  extraReducers: (builder) => {
    builder
      // Stock
      .addCase(fetchStockPrices.pending, (state) => { state.stockStatus = 'loading' })
      .addCase(fetchStockPrices.fulfilled, (state, action) => {
        for (const item of action.payload) {
          state.stockPrices[item.symbol] = item.price
          state.stockUpdatedAt[item.symbol] = item.updated_at
        }
        state.stockStatus = 'succeeded'
        state.stockLastFetchedAt = Date.now()
      })
      .addCase(fetchStockPrices.rejected, (state) => { state.stockStatus = 'failed' })
      // Crypto
      .addCase(fetchCryptoPrices.pending, (state) => { state.cryptoStatus = 'loading' })
      .addCase(fetchCryptoPrices.fulfilled, (state, action) => {
        for (const item of action.payload) {
          state.cryptoPrices[item.symbol] = item.price
          state.cryptoUpdatedAt[item.symbol] = item.updated_at
        }
        state.cryptoStatus = 'succeeded'
        state.cryptoLastFetchedAt = Date.now()
      })
      .addCase(fetchCryptoPrices.rejected, (state) => { state.cryptoStatus = 'failed' })
      // FX
      .addCase(fetchFxRates.pending, (state) => { state.fxStatus = 'loading' })
      .addCase(fetchFxRates.fulfilled, (state, action) => {
        for (const item of action.payload as FxRate[]) {
          state.fxRates[item.pair] = item.rate
          state.fxUpdatedAt[item.pair] = item.updated_at
        }
        state.fxStatus = 'succeeded'
        state.fxLastFetchedAt = Date.now()
      })
      .addCase(fetchFxRates.rejected, (state) => { state.fxStatus = 'failed' })
  },
})

export const { invalidatePrices, upsertCryptoPrice } = pricesSlice.actions
export default pricesSlice.reducer
